import {
  calculateTileLayout,
  GRID_GAP,
  useBaseStore,
  type TileLayout,
} from '@cometchat/calls-sdk-core';
import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type JSX,
} from 'react';
import { FixedSizeGrid } from 'react-window';
import type { TileComponentProps } from './type';

const TileWrapper = ({
  columnIndex,
  rowIndex,
  style,
  data,
}: {
  columnIndex: number;
  rowIndex: number;
  style: React.CSSProperties;
  data: {
    tileCount: number;
    columnCount: number;
    rowCount: number;
    allocatedWidth: number;
    allocatedHeight: number;
    TileComponent: (props: TileComponentProps) => JSX.Element;
  };
}) => {
  const { tileCount, columnCount, rowCount, allocatedWidth, TileComponent } =
    data;
  const tileIndex = rowIndex * columnCount + columnIndex;
  if (tileIndex + 1 > tileCount) {
    return null;
  }
  const isLastRow = rowIndex === rowCount - 1;
  let horizontalOffset = 0;
  let widthToIncrease = 0;

  if (isLastRow) {
    const itemsInLastRow = tileCount - columnCount * rowIndex;
    if (
      itemsInLastRow < columnCount &&
      typeof style.height === 'number' &&
      typeof style.width === 'number'
    ) {
      const allItemWidth = Math.min(
        style.height * (16 / 9) * itemsInLastRow,
        allocatedWidth,
      );
      const singleItemWidth = allItemWidth / itemsInLastRow;
      widthToIncrease = singleItemWidth - style.width;
      horizontalOffset =
        (allocatedWidth - allItemWidth) / 2 + widthToIncrease * columnIndex;
    }
  }
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style,
        left:
          typeof style.left === 'number'
            ? style.left + columnIndex * 0 + horizontalOffset
            : style.left,
        width:
          typeof style.width === 'number'
            ? style.width + widthToIncrease
            : style.width,
        padding: GRID_GAP >> 1,
        boxSizing: 'border-box',
      }}>
      <TileComponent
        columnIndex={columnIndex}
        rowIndex={rowIndex}
        index={tileIndex}
      />
    </div>
  );
};

export function TileView<T>({
  TileComponent,
  listData,
  keyExtractor,
}: {
  TileComponent: (props: TileComponentProps) => JSX.Element;
  listData: T[];
  keyExtractor: (item: T) => string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [tileLayout, setTileLayout] = useState<TileLayout>({
    rows: 1,
    columns: 1,
    tileWidth: 0,
    tileHeight: 0,
    paddingVertical: 0,
    allocatedWidth: 0,
    allocatedHeight: 0,
  });

  useEffect(() => {
    useBaseStore.setState({
      tileDimensions: {
        tileHeight: tileLayout.tileHeight,
        tileWidth: tileLayout.tileWidth,
      },
    });
  }, [tileLayout.tileHeight, tileLayout.tileWidth]);

  useLayoutEffect(() => {
    const gridContainer = ref.current;
    if (!gridContainer) {
      return;
    }
    const observer = new ResizeObserver(entries => {
      setTileLayout(
        calculateTileLayout(
          entries[0].borderBoxSize[0].inlineSize,
          entries[0].borderBoxSize[0].blockSize,
          listData.length,
        ),
      );
    });
    observer.observe(gridContainer);

    return () => {
      observer.unobserve(gridContainer);
    };
  }, [listData.length, ref]);

  return (
    <div
      className="cometchat-calls-tile-grid-container"
      ref={ref}
      style={{
        height:
          'calc(100% - var(--cometchat-calls-call-footer-height) - var(--cometchat-calls-call-header-height))',
        width: '100%',
        display: 'flex',
        paddingTop: tileLayout.paddingVertical,
        paddingBottom: tileLayout.paddingVertical,
        boxSizing: 'border-box',
        justifyContent: 'center',
      }}>
      <FixedSizeGrid
        initialScrollLeft={0}
        initialScrollTop={0}
        overscanRowCount={1}
        columnCount={tileLayout.columns}
        columnWidth={tileLayout.tileWidth + GRID_GAP}
        height={tileLayout.allocatedHeight}
        rowCount={tileLayout.rows}
        rowHeight={tileLayout.tileHeight + GRID_GAP}
        itemKey={({ rowIndex, columnIndex, data }) => {
          const tileIndex = rowIndex * data.columnCount + columnIndex;
          if (tileIndex + 1 > data.tileCount) {
            return `empty-${rowIndex}-${columnIndex}`;
          }
          return keyExtractor(listData[tileIndex]);
        }}
        width={tileLayout.allocatedWidth}
        onItemsRendered={({
          visibleRowStartIndex,
          visibleRowStopIndex,
          visibleColumnStartIndex,
          visibleColumnStopIndex,
        }) => {
          const startIndex =
            visibleRowStartIndex * tileLayout.columns + visibleColumnStartIndex;
          const stopIndex =
            visibleRowStopIndex * tileLayout.columns + visibleColumnStopIndex;
          useBaseStore.setState({
            visibleParticipants: {
              startIndex,
              endIndex: stopIndex,
            },
          });
        }}
        style={{
          overflow: 'scroll',
          scrollbarWidth: 'none',
        }}
        itemData={{
          tileCount: listData.length,
          columnCount: tileLayout.columns,
          rowCount: tileLayout.rows,
          allocatedWidth: tileLayout.allocatedWidth,
          allocatedHeight: tileLayout.allocatedHeight,
          TileComponent,
        }}>
        {TileWrapper}
      </FixedSizeGrid>
    </div>
  );
}
